import {IEntityMetaData, IEntityMetaDataChangeEvent} from "./PA.MD.IF.Entity";
import {
    WB_AttributeTypeRow,
    WB_DataObjectRow,
    WB_EntityAttributeTypeRow,
    WB_EntityRow,
    WB_UIAppExecutableRow
} from "../PA.Core/PAdbSchemaRows";
import {IMessageProvider, MessageType} from "../PA.Core/PA.Core.IF.Basic";
import {PAdbContext} from "../PA.Core/PAdbContext";
import * as _ from "lodash"
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {ESRecordResolver} from "../PA.Core/lib/ESRecordResolver";
import {
    esWB_AttributeType,
    esWB_DataObject,
    esWB_DataObject_Column,
    esWB_DataObject_Column_Text,
    esWB_DataObject_Message,
    esWB_DataObject_Text,
    esWB_Entity,
    esWB_Entity_AttributeType,
    esWB_UI_AppExecutable
} from "../PA.Core/PAdbSchema";
import {BehaviorSubject} from "rxjs";

export class EntityMetaData
    implements
        IEntityMetaData
{
    private _dbContext: PAdbContext
    private _entityRow: WB_EntityRow
    private _loadMessageProvider: MessageProvider
    private _dctAttributeTypes: {[wbId: number]: WB_AttributeTypeRow}
    private _onPropertyChanged: BehaviorSubject<IEntityMetaDataChangeEvent>;
    private _validateMessageProvider: MessageProvider
    private _bSaved: boolean
    private _bDeleted: boolean

    constructor(entityRow: WB_EntityRow) {
        this._entityRow = entityRow
        this._dbContext = <PAdbContext> entityRow.dataStore()
        this._loadMessageProvider = new MessageProvider()
        this._dctAttributeTypes = {}
        this._onPropertyChanged = new BehaviorSubject<IEntityMetaDataChangeEvent>(undefined)
        this._validateMessageProvider = new MessageProvider()
        this._bSaved = false
        this._bDeleted = false
    }

    get LoadMessageProvider(): IMessageProvider { return this._loadMessageProvider }

    async OnLoadComplete(): Promise<void> {
        let dbContext = this._dbContext
        let entityRow = this._entityRow

        let rr = new ESRecordResolver(dbContext, {
            standardConditions: {
                [esWB_Entity]: {DELETED_IND: {neq: '1'}},
                [esWB_Entity_AttributeType]: {DELETED_IND: {neq: '1'}},
                [esWB_AttributeType]: {DELETED_IND: {neq: '1'}},
                [esWB_DataObject]: {DELETED_IND: {neq: '1'}},
                [esWB_DataObject_Column]: {DELETED_IND: {neq: '1'}},
                [esWB_UI_AppExecutable]: {DELETED_IND: {neq: '1'}}
            },
            dctExcludeHasManyNamedRelations: {
                [esWB_DataObject]: [esWB_DataObject_Message, esWB_DataObject_Text],
                [esWB_DataObject_Column]: [esWB_DataObject_Column_Text]
            }
        })
        await rr.findAllWithRelationships(esWB_Entity, [entityRow.cif.WB_ENTITY_ID])

        let arrAttrM = <WB_AttributeTypeRow[]> await rr.queryAllWithRelationships(esWB_AttributeType, {
            where: {
                WB_MDSET_ID: entityRow.cif.WB_MDSET_ID,
                ENTITY_DIMENSION: entityRow.cif.ENTITY_DIMENSION,
                DELETED_IND: {neq: '1'}
            }
        })

        _.forEach(arrAttrM, (a) => {
            this._dctAttributeTypes[a.cif.WB_ATTRIBUTEM_ID] = a
        })

        //-- Get Data Object
        let arrDataObject = <WB_DataObjectRow[]> await rr.findAllWithRelationships(esWB_DataObject_Message, [entityRow.cif.WB_DATAOBJECT_ID])

        //-- Get App Exec
        let arrAppExec = <WB_UIAppExecutableRow[]> await rr.findAllWithRelationships(esWB_UI_AppExecutable, [entityRow.cif.WB_UI_APPEXEC_ID])

        //--

    }

    get WB_EntityRow(): WB_EntityRow { return this._entityRow }

    get AttributeTypes(): WB_AttributeTypeRow[] {
        let dctAttrM: {[wbId: number]: WB_AttributeTypeRow} = {}
        _.forEach(this._entityRow.WB_EntityAttributeTypeRows, (a) => {

            if((!a.isActive) || _.isEmpty(a.WB_AttributeTypeRow))
                return

            let wbAttrMId = a.WB_AttributeTypeRow.cif.WB_ATTRIBUTEM_ID
            if(!(wbAttrMId in dctAttrM))
                dctAttrM[wbAttrMId] = a.WB_AttributeTypeRow
        })

        return  _.sortBy(_.values(dctAttrM), (a) => a.cif.ATTRIBUTEM_NAME)
    }

    get AllAttributeTypes(): WB_AttributeTypeRow[] {
        return  _.sortBy(_.values(this._dctAttributeTypes), (a) => a.cif.ATTRIBUTEM_NAME)
    }

    get OnPropertyChanged(): BehaviorSubject<IEntityMetaDataChangeEvent>{ return this._onPropertyChanged }


    get ValidateMessageProvider(): MessageProvider { return this._validateMessageProvider  }
    async Validate(): Promise<void> {
        let mp = this._validateMessageProvider
        mp.clearMessages()
        //
        // Two attributes can't be the same id
        // Check for duplicate Entity
        let dbContext = this._dbContext
        let entityRow = this._entityRow

        let dups = <WB_EntityRow[]> await dbContext.findAll(esWB_Entity, {
            where: {
                WB_ENTITY_ID: {neq: entityRow.cif.WB_ENTITY_ID},
                ENTITY_ID: entityRow.cif.ENTITY_ID,
                WB_MDSET_ID: entityRow.cif.WB_MDSET_ID,
                ENTITY_DIMENSION: entityRow.cif.ENTITY_DIMENSION,
                DELETED_IND: {neq: '1'}
            }
        })
        if(dups.length){
            let sDups = _.map(dups, (a) => `${a.cif.ENTITY_NAME} (${a.cif.ENTITY_ID})`).join(",")
            mp.addMessage({
                type: MessageType.Error,
                code: "EntityDupId",
                title: "Entity Id is Duplicated",
                description: `Duplicate Entities (${dups.length}): ` + sDups
            })
        }

    }


    async Save(bBatch?: boolean, options?: {bSkipValidations?: boolean}): Promise<void> {
        let dbContext = this._dbContext

        if(_.isEmpty(this._entityRow))
            return
        this._bSaved = false

        let tasks: Promise<any>[] = []
        if(bBatch){
            tasks.push(dbContext.SaveBatch(esWB_Entity, this._entityRow))
            tasks.push(dbContext.SaveBatch(esWB_Entity_AttributeType, this._entityRow.WB_EntityAttributeTypeRows))
            tasks.push(dbContext.SaveBatch(esWB_AttributeType, this.AllAttributeTypes))
        } else {
            tasks.push(dbContext.Save(esWB_Entity, this._entityRow))
            tasks.push(dbContext.Save(esWB_Entity_AttributeType, this._entityRow.WB_EntityAttributeTypeRows))
            tasks.push(dbContext.Save(esWB_AttributeType, this.AllAttributeTypes))
        }

        await Promise.all(tasks)

        this._bSaved = true
    }

    get bSaved(): boolean { return this._bSaved }


    async addAttributeType(anAttribType: WB_AttributeTypeRow): Promise<void> {
        let dbContext = this._dbContext
        let entityRow = this._entityRow

        if(_.isEmpty(entityRow))
            return

        if(_.isEmpty(anAttribType))
            return

        let wbAttrMId = anAttribType?.cif?.WB_ATTRIBUTEM_ID
        if(_.isUndefined(wbAttrMId))
            return
        if(!(wbAttrMId in this._dctAttributeTypes))
            this._dctAttributeTypes[wbAttrMId] = anAttribType

        // Check if there is a duplicate
        let found = false
        _.forEach(entityRow.WB_EntityAttributeTypeRows, (a) => {
            if(!a.isActive)
                return

            if(_.isEmpty(a.WB_AttributeTypeRow))
                return
            if(a.cif.WB_ATTRIBUTEM_ID == wbAttrMId){
                found = true
                return false
            }
        })

        if(found)
            return

        let rel = <WB_EntityAttributeTypeRow> await dbContext.createRecordWithKey(esWB_Entity_AttributeType, {})
        rel.cif.WB_ENTITY_ID = entityRow.cif.WB_ENTITY_ID
        rel.WB_EntityRow = entityRow
        rel.cif.WB_ATTRIBUTEM_ID = wbAttrMId
        rel.WB_AttributeTypeRow = anAttribType
        rel.cif.MANUAL_IND = '1'
        dbContext.add(esWB_Entity_AttributeType, rel)

        this.OnPropertyChanged.next({EntityMetaData: this, PropertyName: "WB_AttributeTypeRow"})

    }

    async removeAttributeType(anAttribType: WB_AttributeTypeRow): Promise<void> {
        let dbContext = this._dbContext
        let entityRow = this._entityRow

        if(_.isEmpty(entityRow))
            return

        if(_.isEmpty(anAttribType))
            return

        let wbAttrMId = anAttribType?.cif?.WB_ATTRIBUTEM_ID
        if(_.isUndefined(wbAttrMId))
            return

        // Check if there is a duplicate
        let found = false
        _.forEach(entityRow.WB_EntityAttributeTypeRows, (a) => {
            if(!a.isActive)
                return

            if(_.isEmpty(a.WB_AttributeTypeRow))
                return
            if(a.cif.WB_ATTRIBUTEM_ID == wbAttrMId){
                a.markDeleted()
                found = true
                return false
            }
        })

        this.OnPropertyChanged.next({EntityMetaData: this, PropertyName: "WB_AttributeTypeRow"})

    }


    async Delete(bSaveNow: boolean, options?: {bSkipValidations?: boolean}): Promise<void> {
        let dbContext = this._dbContext

        if(_.isEmpty(this._entityRow))
            return
        this._bDeleted = false
        this._entityRow.markDeleted()
        _.forEach(this._entityRow.WB_EntityAttributeTypeRows, (a) => {
            a.markDeleted()
        })

        let tasks: Promise<any>[] = []
        if(bSaveNow){
            tasks.push(dbContext.Save(esWB_Entity, this._entityRow))
            tasks.push(dbContext.Save(esWB_Entity_AttributeType, this._entityRow.WB_EntityAttributeTypeRows))
        }

        await Promise.all(tasks)
        this._bDeleted = true
    }

    get bDeleted(): boolean { return this._bDeleted }



}